import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, X, UserX, UserCheck, Edit2, KeyRound } from 'lucide-react';
import { api } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import Avatar from '../components/Avatar';
import type { User } from '../types';

type NewUserForm = {
  email: string;
  first_name: string;
  last_name: string;
  role: 'Administrator' | 'Superuser' | 'User';
  can_see_restricted: boolean;
};

/**
 * A user row as the API returns it: snake_case, straight from the users table. Distinct
 * from `User` in types/index.ts, which is the shape of the *authenticated* user assembled
 * from Cognito claims and uses camelCase. openEdit was annotated with the latter while
 * being handed the former, so `u.canSeeRestricted` type-checked and was undefined at
 * runtime for every row.
 */
type ApiUser = {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  role: 'Administrator' | 'Superuser' | 'User';
  can_see_restricted: boolean;
  is_active?: boolean;
};

type SortCol = 'first_name' | 'last_name' | 'email' | 'role' | 'status';
type SortDir = 'asc' | 'desc';

const ROLE_COLORS: Record<string, { bg: string; color: string }> = {
  Administrator: { bg: '#ede9fe', color: '#6d28d9' },
  Superuser:     { bg: 'var(--sky-50)', color: 'var(--electric)' },
  User:          { bg: 'var(--gray-100)', color: 'var(--gray-700)' },
};

function nameVal(s: string | undefined | null): string {
  const t = (s ?? '').trim();
  // Blank names sort after all named entries regardless of direction
  return t === '' ? '￿' : t.toLowerCase();
}

function sortUsers(users: any[], col: SortCol, dir: SortDir): any[] {
  return [...users].sort((a, b) => {
    let cmp: number;
    if (col === 'status') {
      // Active (true) before Inactive (false) when asc
      const aA = a.is_active !== false;
      const bA = b.is_active !== false;
      cmp = aA === bA ? 0 : aA ? -1 : 1;
    } else if (col === 'first_name' || col === 'last_name') {
      const av = nameVal(a[col]);
      const bv = nameVal(b[col]);
      cmp = av < bv ? -1 : av > bv ? 1 : 0;
    } else {
      const av = (a[col] ?? '').toLowerCase();
      const bv = (b[col] ?? '').toLowerCase();
      cmp = av < bv ? -1 : av > bv ? 1 : 0;
    }
    return dir === 'asc' ? cmp : -cmp;
  });
}

interface SortThProps {
  col: SortCol;
  label: string;
  sortCol: SortCol;
  sortDir: SortDir;
  onSort: (col: SortCol) => void;
}

const SortTh: React.FC<SortThProps> = ({ col, label, sortCol, sortDir, onSort }) => {
  const active = sortCol === col;
  return (
    <th
      onClick={() => onSort(col)}
      style={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}
    >
      {label}
      <span style={{ marginLeft: 4, fontSize: 12, opacity: active ? 0.8 : 0.25 }}>
        {active ? (sortDir === 'asc' ? '↑' : '↓') : '↕'}
      </span>
    </th>
  );
};

const UserManagementPage: React.FC = () => {
  const qc = useQueryClient();
  const { user: currentUser } = useAuth();

  const [showAll, setShowAll]     = useState(false);
  const [sortCol, setSortCol]     = useState<SortCol>('last_name');
  const [sortDir, setSortDir]     = useState<SortDir>('asc');
  const [showCreate, setShowCreate] = useState(false);
  const [editTarget, setEditTarget] = useState<ApiUser | null>(null);
  const [editRole, setEditRole]   = useState<'Administrator' | 'Superuser' | 'User'>('User');
  const [editCanSee, setEditCanSee] = useState(false);
  const [editError, setEditError] = useState('');

  const [form, setForm] = useState<NewUserForm>({
    email: '',
    first_name: '',
    last_name: '',
    role: 'User',
    can_see_restricted: false,
  });
  const [formError, setFormError] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['users', showAll],
    queryFn: () => api.getUsers(showAll ? undefined : { is_active: 'true' }),
  });

  const allUsers: any[] = data?.data ?? [];
  const users = sortUsers(allUsers, sortCol, sortDir);

  const handleSort = (col: SortCol) => {
    if (col === sortCol) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortCol(col);
      setSortDir('asc');
    }
  };

  const createMutation = useMutation({
    mutationFn: () => api.createUser(form),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['users'] });
      setShowCreate(false);
      setForm({ email: '', first_name: '', last_name: '', role: 'User', can_see_restricted: false });
      setFormError('');
    },
    onError: (err: any) => setFormError(err?.response?.data?.error ?? 'Failed to create user.'),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: unknown }) => api.updateUser(id, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['users'] });
      setEditTarget(null);
      setEditError('');
    },
    onError: (err: any) => setEditError(err?.response?.data?.error ?? 'Failed to update user.'),
  });

  const deactivateMutation = useMutation({
    mutationFn: (id: string) => api.deactivateUser(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['users'] }),
    onError: (err: any) => alert(err?.response?.data?.error ?? 'Failed to deactivate user.'),
  });

  const reactivateMutation = useMutation({
    mutationFn: (id: string) => api.reactivateUser(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['users'] }),
    onError: (err: any) => alert(err?.response?.data?.error ?? 'Failed to reactivate user.'),
  });

  const resetPwMutation = useMutation({
    mutationFn: (id: string) => api.resetPassword(id),
    onSuccess: () => alert('Password reset email sent.'),
    onError: (err: any) => alert(err?.response?.data?.error ?? 'Failed to reset password.'),
  });

  const openEdit = (u: ApiUser) => {
    setEditTarget(u);
    setEditRole(u.role as 'Administrator' | 'Superuser' | 'User');
    // The API row carries can_see_restricted (snake_case); canSeeRestricted is the shape of
    // the *authenticated* user in types/index.ts, not of a row in this table. Reading the
    // wrong one yielded undefined for every user, so the checkbox always opened unticked --
    // and saving the modal then sent can_see_restricted:false, silently revoking clearance
    // from anyone whose record an administrator merely opened and saved.
    setEditCanSee(u.can_see_restricted);
    setEditError('');
  };

  const displayName = (first: string, last: string) => {
    const f = first.trim();
    const l = last.trim();
    return f || l ? `${f} ${l}`.trim() : null;
  };

  return (
    <div style={{ paddingBottom: 32 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <h2 style={{ margin: 0, fontSize: 18 }}>
          Users
          {users.length > 0 && (
            <span style={{ fontSize: 14, color: 'var(--gray-500)', fontWeight: 400, marginLeft: 8 }}>({users.length})</span>
          )}
        </h2>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 14, cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={showAll}
              onChange={e => setShowAll(e.target.checked)}
            />
            Show inactive users
          </label>
          <button className="btn btn-primary" onClick={() => setShowCreate(true)}>
            <Plus size={14} />
            New User
          </button>
        </div>
      </div>

      <div className="card" style={{ padding: 0 }}>
        <table className="tbl">
          <thead>
            <tr>
              <SortTh col="first_name" label="First"  sortCol={sortCol} sortDir={sortDir} onSort={handleSort} />
              <SortTh col="last_name"  label="Last"   sortCol={sortCol} sortDir={sortDir} onSort={handleSort} />
              <SortTh col="email"      label="Email"  sortCol={sortCol} sortDir={sortDir} onSort={handleSort} />
              <th>Role</th>
              <th>Restricted</th>
              <SortTh col="status"     label="Status" sortCol={sortCol} sortDir={sortDir} onSort={handleSort} />
              <th></th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={7} style={{ textAlign: 'center', padding: 32, color: 'var(--gray-500)' }}>Loading…</td>
              </tr>
            ) : users.length === 0 ? (
              <tr>
                <td colSpan={7} style={{ textAlign: 'center', padding: 32, color: 'var(--gray-500)' }}>No users</td>
              </tr>
            ) : (
              users.map((u: any) => {
                const roleColors = ROLE_COLORS[u.role] ?? ROLE_COLORS.User;
                const isSelf   = u.email === currentUser?.email;
                const inactive = u.is_active === false;
                const fn = u.first_name?.trim() || '';
                const ln = u.last_name?.trim()  || '';
                const avatarName = displayName(fn, ln) ?? u.email;
                return (
                  <tr key={u.id} style={inactive ? { opacity: 0.55 } : undefined}>
                    <td style={{ fontWeight: 500 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <Avatar name={avatarName} size={26} />
                        {fn || <span style={{ color: 'var(--gray-500)' }}>—</span>}
                      </div>
                    </td>
                    <td style={{ fontWeight: 500 }}>
                      {ln || <span style={{ color: 'var(--gray-500)' }}>—</span>}
                    </td>
                    <td style={{ fontSize: 14, color: 'var(--gray-600)' }}>{u.email}</td>
                    <td>
                      <span className="pill" style={{ background: roleColors.bg, color: roleColors.color }}>
                        {u.role}
                      </span>
                    </td>
                    <td>
                      {u.can_see_restricted
                        ? <span style={{ color: 'var(--green)', fontSize: 14 }}>✓ Granted</span>
                        : <span style={{ color: 'var(--gray-500)', fontSize: 14 }}>—</span>}
                    </td>
                    <td>
                      <span style={{ fontSize: 12 }}>
                        {inactive
                          ? <span style={{ color: 'var(--gray-500)' }}>Inactive</span>
                          : <span style={{ color: 'var(--green)' }}>Active</span>}
                      </span>
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                        {!isSelf && (
                          <button
                            className="btn btn-secondary"
                            style={{ fontSize: 12, padding: '4px 8px' }}
                            title="Edit role"
                            onClick={() => openEdit(u)}
                          >
                            <Edit2 size={12} />
                            Edit
                          </button>
                        )}
                        <button
                          className="btn btn-secondary"
                          style={{ fontSize: 12, padding: '4px 8px' }}
                          title="Reset password"
                          disabled={resetPwMutation.isPending}
                          onClick={() => {
                            if (window.confirm(`Send password reset email to ${u.email}?`)) {
                              resetPwMutation.mutate(u.id);
                            }
                          }}
                        >
                          <KeyRound size={12} />
                          Reset PW
                        </button>
                        {!isSelf && !inactive && (
                          <button
                            className="btn btn-secondary"
                            style={{ fontSize: 12, padding: '4px 8px', color: 'var(--red)' }}
                            title="Deactivate user"
                            disabled={deactivateMutation.isPending}
                            onClick={() => {
                              if (window.confirm(`Deactivate ${avatarName}? They will no longer be able to log in.`)) {
                                deactivateMutation.mutate(u.id);
                              }
                            }}
                          >
                            <UserX size={12} />
                            Deactivate
                          </button>
                        )}
                        {!isSelf && inactive && (
                          <button
                            className="btn btn-secondary"
                            style={{ fontSize: 12, padding: '4px 8px', color: 'var(--green)' }}
                            title="Reactivate user"
                            disabled={reactivateMutation.isPending}
                            onClick={() => {
                              if (window.confirm(`Reactivate ${avatarName}?`)) {
                                reactivateMutation.mutate(u.id);
                              }
                            }}
                          >
                            <UserCheck size={12} />
                            Reactivate
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Edit Role Modal */}
      {editTarget && (
        <div className="modal-overlay" onClick={() => setEditTarget(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <h3 style={{ margin: 0 }}>Edit User — {editTarget.first_name} {editTarget.last_name}</h3>
              <button className="btn btn-ghost" onClick={() => setEditTarget(null)}><X size={16} /></button>
            </div>
            <div style={{ marginBottom: 12 }}>
              <label className="field-label">Role</label>
              <select
                className="field-input"
                value={editRole}
                onChange={(e) => setEditRole(e.target.value as typeof editRole)}
              >
                <option value="User">User</option>
                <option value="Superuser">Superuser</option>
                <option value="Administrator">Administrator</option>
              </select>
            </div>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, marginBottom: 16, cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={editCanSee}
                onChange={(e) => setEditCanSee(e.target.checked)}
              />
              Grant restricted contact access
            </label>
            {editError && <p style={{ color: 'var(--red)', fontSize: 12, margin: '0 0 12px' }}>{editError}</p>}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button className="btn btn-secondary" onClick={() => setEditTarget(null)}>Cancel</button>
              <button
                className="btn btn-primary"
                disabled={updateMutation.isPending}
                onClick={() => updateMutation.mutate({
                  id: editTarget.id,
                  data: { role: editRole, can_see_restricted: editCanSee },
                })}
              >
                {updateMutation.isPending ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create User Modal */}
      {showCreate && (
        <div className="modal-overlay" onClick={() => setShowCreate(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <h3 style={{ margin: 0 }}>Create User</h3>
              <button className="btn btn-ghost" onClick={() => setShowCreate(false)}><X size={16} /></button>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
              <div>
                <label className="field-label">First Name</label>
                <input
                  type="text" className="field-input" value={form.first_name}
                  onChange={(e) => setForm({ ...form, first_name: e.target.value })}
                />
              </div>
              <div>
                <label className="field-label">Last Name</label>
                <input
                  type="text" className="field-input" value={form.last_name}
                  onChange={(e) => setForm({ ...form, last_name: e.target.value })}
                />
              </div>
            </div>

            <div style={{ marginBottom: 12 }}>
              <label className="field-label">Email</label>
              <input
                type="email" className="field-input" value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
              />
            </div>

            <div style={{ marginBottom: 12 }}>
              <label className="field-label">Role</label>
              <select
                className="field-input" value={form.role}
                onChange={(e) => setForm({ ...form, role: e.target.value as NewUserForm['role'] })}
              >
                <option value="User">User</option>
                <option value="Superuser">Superuser</option>
                <option value="Administrator">Administrator</option>
              </select>
            </div>

            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, marginBottom: 16, cursor: 'pointer' }}>
              <input
                type="checkbox" checked={form.can_see_restricted}
                onChange={(e) => setForm({ ...form, can_see_restricted: e.target.checked })}
              />
              Grant restricted contact access
            </label>

            {formError && <p style={{ color: 'var(--red)', fontSize: 12, margin: '0 0 12px' }}>{formError}</p>}

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button className="btn btn-secondary" onClick={() => setShowCreate(false)}>Cancel</button>
              <button
                className="btn btn-primary"
                disabled={!form.email.trim() || !form.first_name.trim() || !form.last_name.trim() || createMutation.isPending}
                onClick={() => createMutation.mutate()}
              >
                {createMutation.isPending ? 'Creating…' : 'Create User'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagementPage;
